import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CommunicationThread = ({ dispute }) => {
  const [newMessage, setNewMessage] = useState('');

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner':
        return 'bg-primary/10 text-primary';
      case 'renter':
        return 'bg-secondary/10 text-secondary';
      case 'admin':
        return 'bg-warning/10 text-warning';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner':
        return 'Home';
      case 'renter':
        return 'User';
      case 'admin':
        return 'Shield';
      default:
        return 'User';
    }
  };

  const handleSendMessage = () => {
    if (!newMessage?.trim()) return;
    // In real implementation, this would send the message via API
    console.log('Sending message:', newMessage);
    setNewMessage('');
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
          Communication Thread
        </h2>
        <p className="font-body text-muted-foreground">
          All messages and responses related to this dispute
        </p>
      </div>

      {/* Messages */}
      <div className="space-y-4 max-h-[500px] overflow-y-auto">
        {dispute?.messages?.map((message) => (
          <div
            key={message?.id}
            className={`rounded-lg p-4 ${
              message?.role === 'owner' ?'bg-primary/5 border border-primary/20 ml-8' :'bg-muted/30 border border-border mr-8'
            }`}
          >
            {/* Message Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className={`flex items-center space-x-1 px-2 py-1 rounded-full ${getRoleColor(message?.role)}`}>
                  <Icon name={getRoleIcon(message?.role)} size={14} strokeWidth={2} />
                  <span className="font-caption text-xs font-semibold">
                    {message?.sender}
                  </span>
                </div>
                <span className="font-caption text-xs text-muted-foreground">
                  {formatTimestamp(message?.timestamp)}
                </span>
              </div>
            </div>

            {/* Message Content */}
            <p className="font-body text-foreground leading-relaxed mb-3">
              {message?.message}
            </p>

            {/* Message Evidence */}
            {message?.evidence?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="font-caption text-xs text-muted-foreground mb-2">
                  Attached Evidence ({message?.evidence?.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {message?.evidence?.map((evidence) => (
                    <div
                      key={evidence?.id}
                      className="flex items-center space-x-2 bg-background rounded px-3 py-1 border border-border"
                    >
                      <Icon
                        name={evidence?.type === 'image' ? 'Image' : evidence?.type === 'video' ? 'Video' : 'FileText'}
                        size={14}
                        className="text-primary"
                        strokeWidth={2}
                      />
                      <span className="font-caption text-xs text-foreground">{evidence?.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {dispute?.messages?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="MessageSquare" size={48} className="text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <p className="font-body text-muted-foreground">
              No messages yet. Start the conversation by responding to the dispute.
            </p>
          </div>
        )}
      </div>

      {/* Message Input */}
      <div className="pt-4 border-t border-border">
        <label className="font-body font-semibold text-foreground mb-2 block">
          Send Message
        </label>
        <div className="flex flex-col sm:flex-row gap-3">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e?.target?.value)}
            placeholder="Type your message here..."
            rows={3}
            className="flex-1 px-4 py-3 rounded-md border border-border bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          />
          <Button
            variant="default"
            iconName="Send"
            onClick={handleSendMessage}
            disabled={!newMessage?.trim()}
            className="sm:self-end"
          >
            Send
          </Button>
        </div>
        <p className="font-caption text-xs text-muted-foreground mt-2">
          All messages are recorded and may be reviewed by platform administrators
        </p>
      </div>

      {/* Communication Guidelines */}
      <div className="bg-muted/30 rounded-lg p-4">
        <h4 className="font-body font-semibold text-foreground mb-2 flex items-center space-x-2">
          <Icon name="Info" size={18} strokeWidth={2} />
          <span>Communication Guidelines</span>
        </h4>
        <ul className="space-y-2 font-body text-sm text-muted-foreground">
          <li className="flex items-start space-x-2">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
            <span>Remain professional and respectful at all times</span>
          </li>
          <li className="flex items-start space-x-2">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
            <span>Focus on facts and evidence, not personal opinions</span>
          </li>
          <li className="flex items-start space-x-2">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
            <span>Respond promptly to maintain good communication</span>
          </li>
          <li className="flex items-start space-x-2">
            <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" strokeWidth={2} />
            <span>Avoid threatening or harassing language</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default CommunicationThread;